import React, { useRef, useState } from 'react';
import { Editor } from '@tinymce/tinymce-react';

// Import TinyMCE core and plugins locally
import 'tinymce/tinymce';
import 'tinymce/models/dom';
import 'tinymce/themes/silver';
import 'tinymce/icons/default';

// Import plugins
import 'tinymce/plugins/table';
import 'tinymce/plugins/lists';
import 'tinymce/plugins/link';
import 'tinymce/plugins/autolink';

// Import skin CSS
import 'tinymce/skins/ui/oxide/skin.min.css';

const RichTextEditor = ({ 
  value, 
  onChange, 
  placeholder = "Inserisci il testo...",
  height = "200px",
  readOnly = false,
  enableTables = true
}) => {
  const editorRef = useRef(null);
  const [showTableDialog, setShowTableDialog] = useState(false);
  const [tableRows, setTableRows] = useState(3);
  const [tableCols, setTableCols] = useState(3);

  const handleEditorChange = (content) => {
    if (onChange) {
      onChange(content);
    }
  };

  const insertTable = () => {
    const editor = editorRef.current;
    if (editor) {
      // Create table HTML manually
      let tableHTML = '<table style="border-collapse: collapse; width: 100%; margin: 10px 0;">';
      for (let r = 0; r < tableRows; r++) {
        tableHTML += '<tr>';
        for (let c = 0; c < tableCols; c++) {
          const isHeader = r === 0;
          const style = isHeader 
            ? 'border: 1px solid #dee2e6; padding: 8px 12px; background-color: #f8f9fa; font-weight: bold;'
            : 'border: 1px solid #dee2e6; padding: 8px 12px;';
          tableHTML += `<td style="${style}">${isHeader ? `Col ${c + 1}` : ''}</td>`;
        }
        tableHTML += '</tr>';
      }
      tableHTML += '</table><p></p>';
      
      editor.insertContent(tableHTML);
      setShowTableDialog(false);
    }
  };

  // Parse height to number
  const heightNum = parseInt(height.replace('px', '')) || 200;

  if (readOnly) {
    return (
      <div 
        className="prose prose-sm max-w-none p-3 bg-slate-50 rounded-lg border border-slate-200"
        dangerouslySetInnerHTML={{ __html: value || '' }}
      />
    );
  }

  return (
    <div className="rich-text-editor">
      <Editor
        onInit={(evt, editor) => editorRef.current = editor}
        value={value || ''}
        onEditorChange={handleEditorChange}
        init={{
          height: heightNum + 50,
          menubar: false,
          plugins: 'table lists link autolink',
          toolbar: enableTables 
            ? 'blocks | bold italic underline strikethrough | forecolor backcolor | alignleft aligncenter alignright | bullist numlist | outdent indent | customtable | link | removeformat'
            : 'blocks | bold italic underline strikethrough | forecolor backcolor | alignleft aligncenter alignright | bullist numlist | outdent indent | link | removeformat',
          toolbar_mode: 'wrap',
          // FIX: Proper block_formats configuration
          block_formats: 'Paragrafo=p; Titolo 1=h1; Titolo 2=h2; Titolo 3=h3; Titolo 4=h4',
          placeholder: placeholder,
          content_style: `
            body { 
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; 
              font-size: 14px; 
              padding: 8px;
              margin: 0;
            }
            table { 
              border-collapse: collapse; 
              width: 100%; 
              margin: 10px 0;
            }
            table td, table th { 
              border: 1px solid #dee2e6; 
              padding: 8px 12px; 
              min-width: 50px;
            }
            h1 { font-size: 1.5em; margin: 0.5em 0; color: #f97316; font-weight: bold; }
            h2 { font-size: 1.3em; margin: 0.5em 0; color: #1e40af; font-weight: bold; }
            h3 { font-size: 1.1em; margin: 0.5em 0; font-weight: bold; }
            h4 { font-size: 1em; margin: 0.5em 0; font-weight: bold; }
            p { margin: 0.5em 0; }
            ul, ol { margin: 0.5em 0; padding-left: 20px; }
            li { margin: 0.3em 0; }
          `,
          // Extended color palette
          color_map: [
            '000000', 'Nero',
            '333333', 'Grigio scuro',
            '666666', 'Grigio',
            '999999', 'Grigio chiaro',
            'FFFFFF', 'Bianco',
            'F97316', 'Arancione',
            'EA580C', 'Arancione scuro',
            '1E40AF', 'Blu',
            '1D4ED8', 'Blu chiaro',
            '059669', 'Verde',
            '10B981', 'Verde chiaro',
            'DC2626', 'Rosso',
            'EF4444', 'Rosso chiaro',
            '7C3AED', 'Viola',
            'DB2777', 'Rosa',
            'F8F9FA', 'Grigio sfondo',
            'FEF3C7', 'Giallo sfondo',
            'DBEAFE', 'Blu sfondo',
            'D1FAE5', 'Verde sfondo',
            'FEE2E2', 'Rosso sfondo'
          ],
          // TinyMCE configuration
          branding: false,
          promotion: false,
          statusbar: false,
          resize: false,
          skin: false,
          content_css: false,
          license_key: 'gpl',
          // Force formats to show
          style_formats_merge: true,
          style_formats: [
            { title: 'Titolo 1', block: 'h1' },
            { title: 'Titolo 2', block: 'h2' },
            { title: 'Titolo 3', block: 'h3' },
            { title: 'Titolo 4', block: 'h4' },
            { title: 'Paragrafo', block: 'p' }
          ],
          // Custom setup
          setup: function(editor) {
            // Custom table button that opens our dialog
            editor.ui.registry.addButton('customtable', {
              icon: 'table',
              tooltip: 'Inserisci tabella',
              onAction: function() {
                setShowTableDialog(true);
              }
            });
          }
        }}
      />
      
      {/* Custom Table Dialog */}
      {showTableDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999]" onClick={() => setShowTableDialog(false)}>
          <div className="bg-white rounded-lg shadow-xl p-6 w-80" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-semibold mb-4">Inserisci Tabella</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Righe
                </label>
                <input
                  type="number"
                  min="1"
                  max="20"
                  value={tableRows}
                  onChange={(e) => setTableRows(parseInt(e.target.value) || 1)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Colonne
                </label>
                <input
                  type="number"
                  min="1"
                  max="20"
                  value={tableCols}
                  onChange={(e) => setTableCols(parseInt(e.target.value) || 1)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              <div className="text-sm text-gray-500">
                Anteprima: {tableRows} righe × {tableCols} colonne
              </div>
            </div>
            
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowTableDialog(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Annulla
              </button>
              <button
                onClick={insertTable}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Inserisci
              </button>
            </div>
          </div>
        </div>
      )}
      
      <style>{`
        .rich-text-editor .tox-tinymce {
          border-radius: 0.5rem;
          border-color: #e2e8f0;
        }
        .rich-text-editor .tox-toolbar__primary {
          background: #f8fafc !important;
        }
        .rich-text-editor .tox-edit-area__iframe {
          background: #fff;
        }
        .rich-text-editor .tox .tox-tbtn {
          margin: 2px;
        }
        .rich-text-editor .tox-statusbar {
          display: none !important;
        }
        .rich-text-editor .tox-promotion,
        .rich-text-editor .tox-statusbar__branding {
          display: none !important;
        }
        /* Ensure blocks dropdown works */
        .rich-text-editor .tox .tox-tbtn--bespoke .tox-tbtn__select-label {
          width: auto;
          min-width: 80px;
        }
      `}</style>
    </div>
  );
};

export default RichTextEditor;
